import Tracker from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'
import trackerVuex from '@openreplay/tracker-vuex'
import type { App } from 'vue'

const ENABLE_OPENREPLAY_PRIVATE =
    (import.meta.env.VITE_OPENREPLAY_PRIVATE !== 'false')
const ENABLE_OPENREPLAY_VUEX =
    (import.meta.env.VITE_OPENREPLAY_VUEX === 'true')

let tracker: Tracker|null = null
let trackerStore: any|null = null
let identifyName: string|null = null
let identifyOptions: Record<string, string>|null = null
const productionDataPrivate: any = {}

class OpenReplay {

  install(app: App<Element>) {
    const isInternalHost = internalHost()

    // Install tracker apis.
    app.config.globalProperties.$track = this.track
    app.config.globalProperties.$issue = this.issue
    app.config.globalProperties.$error = this.error
    app.config.globalProperties.$productionDataPrivate = productionDataPrivate

    // Enable data shrouding.
    if (ENABLE_OPENREPLAY_PRIVATE || !isInternalHost) {
      productionDataPrivate['data-openreplay-hidden'] = ''
    }
  }

  init(excludeContentRE: RegExp = /\/api\//) {
    if (!tracker) {
      if (!window.navigator.userAgent.match(/headless/i)) { // do not record headless testing sessions
        const isInternalHost = internalHost()

        // Initialize
        const options = {
          resourceBaseHref: useRuntimeConfig()?.app?.cdnURL || window.location.origin
        }
        tracker = new Tracker({
          projectKey: useRuntimeConfig()?.public?.openReplay?.projectKey,
          ingestPoint: useRuntimeConfig()?.public?.openReplay?.ingestPoint,
          revID: import.meta.env.VITE_BUILD_VERSION,
          network: {
            failuresOnly: false,
            sessionTokenHeader: false,
            capturePayload: !!isInternalHost,
            sanitizer(data: any): any {
              if (!isInternalHost) {
                data.request.headers.Authorization = null
                if (data.url.match(excludeContentRE)) {
                  data.request.body = null
                }
                data.response.body = null
              }
              return data
            }
          },
          defaultInputMode: 0,
          ...options
        })
        const trackerAssistOptions = {}
        tracker.use(trackerAssist(trackerAssistOptions))

        // Vuex.
        if (ENABLE_OPENREPLAY_VUEX) {
          trackerStore = tracker.use(trackerVuex())
        }

        // Start trackin'  NB: we can authenticate before we load the tracker runtime.
        if (identifyName) {
          tracker.start({
            userID: identifyOptions?.name || identifyName,
            metadata: {
              ...(identifyOptions || {}),
              origin: window.location.origin
            }
          })
        } else {
          tracker.start({
            metadata: {
              origin: window.location.origin
            }
          })
        }
      }
    }
  }

  terminate() {
    if (tracker) {
      tracker.stop()
      tracker = null
    }
  }

  addVuexPlugin(plugins: Array<any>, name: string) {
    if (ENABLE_OPENREPLAY_VUEX && trackerStore) {
      plugins.push(trackerStore(name))
    }
  }

  track(eventName: string, arg: any = null) {
    return tracker?.event(eventName, arg || {})
  }

  issue(eventName: string, arg: any) {
    return tracker?.issue(eventName, arg || {})
  }

  error(e: Error, arg: any) {
    return tracker?.handleError(e, arg || {})
  }

  identify(installerNameOption: string, options: Record<string, string> | null = null) {
    identifyName = installerNameOption
    identifyOptions = options
    tracker?.setUserID(identifyOptions?.name || identifyName)
    Object.entries(identifyOptions || {}).forEach(([k, v]) => {
      tracker?.setMetadata(k, v)
    })
  }

  get sessionURL() {
    return tracker?.getSessionURL({ withCurrentTime: true })
  }
}

const openReplay = new OpenReplay()
export default openReplay
